import { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { getVacationCalendar } from '../../../api/vacations';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

export default function CalendarTab() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [calendario, setCalendario] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVacationCalendar(year, month);
      setCalendario(res.data || {});
    } catch { setCalendario({}); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1;  y++; }
    if (m < 1)  { m = 12; y--; }
    setMonth(m);
    setYear(y);
  };

  const daysInMonth  = getDaysInMonth(year, month - 1);
  const firstDay     = getFirstDayOfMonth(year, month - 1);
  const todayStr     = today.toISOString().split('T')[0];

  // Ausencias únicas del mes (una por absence_id)
  const ausenciasDelMes = Object.values(calendario).flat()
    .reduce((acc, a) => {
      if (!acc.find(x => x.absence_id === a.absence_id)) acc.push(a);
      return acc;
    }, [])
    .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-teal-500" />
            Calendario de Vacaciones
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => changeMonth(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-sm font-semibold text-gray-700 w-36 text-center">
              {MESES[month - 1]} {year}
            </span>
            <button onClick={() => changeMonth(1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Leyenda */}
        <div className="flex gap-4 mb-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-teal-500 inline-block" /> Empleado en vacaciones
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full border-2 border-orange-400 inline-block" /> Hoy
          </span>
        </div>

        {/* Grid días semana */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DIAS_SEMANA.map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Grid días */}
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {/* Celdas vacías inicio */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e${i}`} className="aspect-square" />
            ))}

            {/* Días del mes */}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const mm   = String(month).padStart(2, '0');
              const dd   = String(day).padStart(2, '0');
              const key  = `${year}-${mm}-${dd}`;
              const abs  = calendario[key] || [];
              const isToday = key === todayStr;

              return (
                <div key={day}
                  className={`aspect-square rounded-lg p-1 text-[11px] relative border transition-all
                    ${abs.length > 0 ? 'bg-teal-50 border-teal-200' : 'border-transparent hover:bg-gray-50'}
                    ${isToday ? 'ring-2 ring-orange-400' : ''}
                  `}
                >
                  <span className={`font-semibold ${isToday ? 'text-orange-500' : 'text-gray-600'}`}>{day}</span>
                  {abs.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-0.5">
                      {abs.slice(0, 3).map((a, idx) => (
                        <div key={idx}
                          className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-white flex-shrink-0"
                          title={`${a.nombre} ${a.apellido}`}
                        >
                          {a.foto ? (
                            <img
                              src={a.foto}
                              alt={`${a.nombre} ${a.apellido}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-teal-500 flex items-center justify-center text-white font-bold"
                              style={{ fontSize: '7px' }}
                            >
                              {a.nombre?.[0]}{a.apellido?.[0]}
                            </div>
                          )}
                        </div>
                      ))}
                      {abs.length > 3 && (
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold flex-shrink-0"
                          style={{ fontSize: '7px' }}
                        >
                          +{abs.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lista de ausencias del mes */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          Vacaciones en {MESES[month - 1]} {year}
          <span className="ml-auto text-xs font-normal text-gray-400">{ausenciasDelMes.length} empleado(s)</span>
        </h4>

        {ausenciasDelMes.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No hay vacaciones aprobadas para este mes</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ausenciasDelMes.map(a => (
              <div key={a.absence_id}
                className="bg-teal-50 border-l-4 border-teal-400 rounded-lg p-3 flex items-start gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 text-xs font-bold flex-shrink-0">
                  {a.nombre?.[0]}{a.apellido?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{a.nombre} {a.apellido}</p>
                  {a.cargo && <p className="text-xs text-gray-500 truncate">{a.cargo}</p>}
                  <p className="text-xs text-teal-700 mt-0.5">
                    {new Date(a.fecha_inicio + 'T00:00:00').toLocaleDateString('es-DO', { day:'2-digit', month:'short' })}
                    {' → '}
                    {new Date(a.fecha_fin + 'T00:00:00').toLocaleDateString('es-DO', { day:'2-digit', month:'short' })}
                    <span className="ml-2 font-semibold">({a.dias} días)</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
